// Straight from types.gen rather than the generated index, which would pull
// the whole HTTP client in behind one enum (same reason entity-store.ts does).
import {
    type BusinessEntity,
    type BusinessEntityRelationship,
    RelationshipDirection,
} from "@/lib/openapi/gen/types.gen";

/**
 * The optimistic stand-in for one domain object while its save is still in
 * flight: only the fields EntitiesDiagram draws, and only the ones that
 * actually changed.
 *
 * Patching the OBJECTS rather than the Mermaid source is deliberate. The
 * Mermaid text is derived — `buildMermaidSource()` regenerates it from the
 * entities list on every render — so a patch applied there would be wiped by
 * the very next redraw. The entities list is the input everything else is a
 * pure function of, so patching it makes the label, the edges and the
 * signature all follow on their own.
 */
export interface CanvasPatch {
    name?: string;
    relationships?: BusinessEntityRelationship[];
}

const relKey = (relationship: BusinessEntityRelationship) =>
    `${relationship.direction}\0${relationship.type}\0${relationship.other_id}`;

/**
 * The same edge as seen from the object at the other end.
 *
 * Relationships are stored once server-side (one row per edge) and read back
 * onto BOTH objects — outgoing on the source, incoming on the target. So an
 * edit to one object's list always implies a matching change on its
 * counterparts, and an optimistic patch has to make that change itself or the
 * canvas draws a half-edge until the refetch lands.
 */
const mirrored = (
    relationship: BusinessEntityRelationship,
    ownerId: string
) => ({
    counterpartId: relationship.other_id,
    relationship: {
        type: relationship.type,
        other_id: ownerId,
        direction:
            relationship.direction === RelationshipDirection.OUTGOING
                ? RelationshipDirection.INCOMING
                : RelationshipDirection.OUTGOING,
    } satisfies BusinessEntityRelationship,
});

/**
 * The entities list as the canvas should show it right now: server truth with
 * every outstanding optimistic patch laid over the top.
 *
 * Returns the ORIGINAL array, by reference, when no patch changes anything.
 * That is what makes a landed save free: once the refetch has brought the
 * server up to the value we already guessed, this produces a structurally
 * identical list, `buildDiagramSignature` is unchanged, and dropping the patch
 * costs no second re-render. Callers also use that identity as the "has this
 * patch converged?" test.
 */
export const applyCanvasPatches = (
    entities: BusinessEntity[],
    patches: Readonly<Record<string, CanvasPatch>>
): BusinessEntity[] => {
    const patchedIds = Object.keys(patches);
    if (patchedIds.length === 0) return entities;

    // Copy-on-write: an object nobody patched keeps its original reference.
    const drafts = new Map<string, BusinessEntity>();
    let changed = false;

    const draftFor = (identity: string): BusinessEntity | undefined => {
        const existing = drafts.get(identity);
        if (existing) return existing;
        const original = entities.find((e) => e.identity === identity);
        if (!original) return undefined;
        const draft = {
            ...original,
            relationships: [...original.relationships],
        };
        drafts.set(identity, draft);
        return draft;
    };

    for (const identity of patchedIds) {
        const patch = patches[identity];
        const server = entities.find((e) => e.identity === identity);
        // The object has left this domain's list (deleted, or the user moved
        // on). Its patch has nothing to apply to and is dropped by reconcile.
        if (!server) continue;

        if (patch.name !== undefined && patch.name !== server.name) {
            const draft = draftFor(identity);
            if (draft) {
                draft.name = patch.name;
                changed = true;
            }
        }

        if (!patch.relationships) continue;

        const current =
            drafts.get(identity)?.relationships ?? server.relationships;
        const before = new Set(current.map(relKey));
        const after = new Set(patch.relationships.map(relKey));
        const added = patch.relationships.filter((r) => !before.has(relKey(r)));
        const removed = current.filter((r) => !after.has(relKey(r)));

        // Already converged (the server caught up, or the edit netted out).
        if (added.length === 0 && removed.length === 0) continue;

        const draft = draftFor(identity);
        if (!draft) continue;
        draft.relationships = patch.relationships.map((r) => ({ ...r }));
        changed = true;

        for (const relationship of added) {
            const { counterpartId, relationship: edge } = mirrored(
                relationship,
                identity
            );
            const counterpart = draftFor(counterpartId);
            if (!counterpart) continue;
            if (
                counterpart.relationships.some(
                    (r) => relKey(r) === relKey(edge)
                )
            ) {
                continue;
            }
            counterpart.relationships = [...counterpart.relationships, edge];
        }
        for (const relationship of removed) {
            const { counterpartId, relationship: edge } = mirrored(
                relationship,
                identity
            );
            const counterpart = draftFor(counterpartId);
            if (!counterpart) continue;
            counterpart.relationships = counterpart.relationships.filter(
                (r) => relKey(r) !== relKey(edge)
            );
        }
    }

    if (!changed) return entities;
    return entities.map((entity) => drafts.get(entity.identity) ?? entity);
};
